import React, { useEffect, useState } from 'react';
import SectionHeading from './SectionHeading';
import EmptyState from './EmptyState';
import { toneClasses } from '../lib/tones';
import { Icon } from './Icon';
import type { IconName } from './Icon';

/**
 * `info` is a fact worth recording that is neither an outcome nor a problem
 * (a Polar subscription updated or cancelled).
 * In the admin it comes from a register row's `platform_events.severity`
 * (`severityActivityStatus`); the old `platform_events.status` column that
 * used to store it was dropped in `20260953_events_contract`. The tenant's
 * task feed never produces it, so `taskActivityStatus` has nothing to map to it.
 */
export type ActivityStatus = 'success' | 'error' | 'warning' | 'info' | 'running' | 'neutral';

/**
 * Canonical status icon for an activity feed entry.
 *
 * Single source of truth used across the sidebar, the history modal and the
 * detail modal in both apps. Consumers should NOT reimplement this mapping.
 * Returns a material-symbols name + extra className (spin animation for
 * running).
 */
export function activityStatusIcon(status: ActivityStatus): { name: IconName; className: string } {
  switch (status) {
    case 'running':
      return { name: 'progress_activity', className: 'animate-spin' };
    case 'success':
      return { name: 'check_circle', className: '' };
    case 'error':
      return { name: 'cancel', className: '' };
    case 'warning':
      return { name: 'warning', className: '' };
    case 'info':
      return { name: 'info', className: '' };
    default:
      return { name: 'schedule', className: '' };
  }
}

/** Default ReactNode rendered when a RecentActivityItem does not provide its own icon. */
export function defaultActivityIcon(status: ActivityStatus): React.ReactNode {
  const { name, className } = activityStatusIcon(status);
  return (
    <Icon name={name} className={`!text-[16px] ${className}`} />
  );
}

export interface RecentActivityItem {
  id: string | number;
  icon?: React.ReactNode;
  title: string;
  subtitle?: string;
  /** Optional extra line, e.g. the user/actor who performed the activity. */
  meta?: string;
  timestamp?: string | Date;
  status?: ActivityStatus;
  onClick?: () => void;
}

// Colours come from the shared tone vocabulary. `running` and `info` share the
// `info` tone on purpose: the icon (spinner vs. `info`) is what tells them
// apart. `neutral` stays literal because the tone's grey is not the one the
// feed has always used, and swapping it would restyle every pending task.
const STATUS_ICON_COLOR: Record<ActivityStatus, string> = {
  success: toneClasses.success.icon,
  error: toneClasses.danger.icon,
  warning: toneClasses.warning.icon,
  info: toneClasses.info.icon,
  running: toneClasses.info.icon,
  neutral: 'text-slate-500 dark:text-slate-400',
};

const STATUS_SUBTITLE_COLOR: Record<ActivityStatus, string> = {
  success: toneClasses.success.value,
  error: toneClasses.danger.value,
  warning: toneClasses.warning.value,
  info: toneClasses.info.value,
  running: toneClasses.info.value,
  neutral: 'text-slate-500 dark:text-slate-500',
};

interface Props {
  heading: string;
  headingIcon?: IconName;
  items: RecentActivityItem[];
  loading?: boolean;
  emptyLabel?: string;
  action?: { icon: IconName; title: string; onClick: () => void };
  locale?: string;
  /** If provided, collapsed state persists under this localStorage key. */
  storageKey?: string;
}

function formatTime(ts: string | Date | undefined, locale: string) {
  if (!ts) return '';
  const d = typeof ts === 'string' ? new Date(ts) : ts;
  return d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
}

/**
 * Generic recent-activity list rendered inside an app sidebar.
 *
 * Standard component used by both admin and tenant for the bottom-of-sidebar
 * activity feed. Pass a flat list of items; this component handles styling,
 * loading, empty states, collapse/expand and responsive visibility.
 *
 * - Collapsed by default (click heading to expand).
 * - Hidden on viewports below `md` — the host sidebar keeps working, but this
 *   block disappears on mobile where vertical space is scarce.
 * - Icons are `ReactNode` so consumers can pass any icon library (lucide,
 *   material-symbols, etc.) without coupling the shared component.
 */
export default function RecentActivitySidebar({
  heading,
  headingIcon = 'history',
  items,
  loading = false,
  emptyLabel = 'Sin actividad reciente',
  action,
  locale = 'es-ES',
  storageKey,
}: Props) {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (!storageKey || typeof window === 'undefined') return true;
    const raw = window.localStorage.getItem(storageKey);
    if (raw === null) return true;
    return raw === '1';
  });

  useEffect(() => {
    if (!storageKey || typeof window === 'undefined') return;
    window.localStorage.setItem(storageKey, collapsed ? '1' : '0');
  }, [collapsed, storageKey]);

  return (
    <div className="mt-auto px-4 py-4 hidden md:block">
      <div className="flex items-center justify-between mb-3 gap-2">
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          className="flex items-center gap-2 flex-1 min-w-0 text-left"
          aria-expanded={!collapsed}
        >
          <SectionHeading icon={headingIcon}>{heading}</SectionHeading>
          <Icon name="expand_more" className={`!text-[14px] text-slate-500 dark:text-slate-400 transition-transform ${
              collapsed ? '' : 'rotate-180'
            }`} />
        </button>
        {action ? (
          <button
            type="button"
            onClick={action.onClick}
            title={action.title}
            className="p-1 rounded-full text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors shrink-0"
          >
            <Icon name={action.icon} className="!text-[14px]" />
          </button>
        ) : null}
      </div>

      {collapsed ? null : (
        <div className="space-y-2 max-h-80 overflow-y-auto no-scrollbar">
          {loading && items.length === 0 ? (
            <div className="flex justify-center p-2">
              <Icon name="progress_activity" className="animate-spin !text-[16px] text-slate-400 dark:text-slate-500" />
            </div>
          ) : items.length === 0 ? (
            <EmptyState icon={headingIcon} label={emptyLabel} />
          ) : (
            items.map((item) => {
              const Wrapper: React.ElementType = item.onClick ? 'button' : 'div';
              const status: ActivityStatus = item.status ?? 'neutral';
              const iconColor = STATUS_ICON_COLOR[status];
              const subtitleColor = STATUS_SUBTITLE_COLOR[status];
              const iconNode = item.icon ?? defaultActivityIcon(status);
              return (
                <Wrapper
                  key={item.id}
                  onClick={item.onClick}
                  className="w-full text-left flex items-center gap-3 p-2 rounded-xl transition-all border group animate-in fade-in slide-in-from-left-2 bg-slate-50 border-border-subtle shadow-subtle dark:bg-surface-dark dark:border-border-subtle-dark dark:shadow-none hover:bg-slate-100 hover:border-border-strong dark:hover:bg-slate-800 dark:hover:border-border-strong-dark"
                >
                  <span className={`flex shrink-0 items-center justify-center ${iconColor}`}>
                    {iconNode}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] font-medium text-slate-800 dark:text-slate-100 truncate leading-tight">
                      {item.title}
                    </p>
                    {item.subtitle || item.timestamp ? (
                      <p className={`text-[9px] truncate ${subtitleColor}`}>
                        {item.subtitle}
                        {item.subtitle && item.timestamp ? ' • ' : ''}
                        {formatTime(item.timestamp, locale)}
                      </p>
                    ) : null}
                    {item.meta ? (
                      <p className="text-[9px] truncate text-slate-500 dark:text-slate-400 flex items-center gap-1">
                        <Icon name="person" className="!text-[10px]" />
                        {item.meta}
                      </p>
                    ) : null}
                  </div>
                </Wrapper>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
